import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteIntelligenceRow,
  fetchIntelligenceDraft,
  fetchSubmittedPortfolio,
  saveIntelligenceDraft,
  submitIntelligenceDraft,
  portfolioHasVisibleFacilities,
  updateIntelligenceRow,
  type NiPortfolio,
  type NiPortfolioRow,
} from "@services/networkIntelligence";
import { getCurrentUser } from "@utils/userUtils";
import FacilityNetworkPageShell from "../facilityNetwork/FacilityNetworkPageShell";
import { FN } from "../facilityNetwork/facilityNetworkTheme";
import NetworkIntelligenceLoader from "./NetworkIntelligenceLoader";
import { claimPortfolioHandoff } from "./portfolioHandoff";

const GRADIENT = "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)";

const PREVIEW_WIDTHS = ["30%", "15%", "14%", "16%", "15%", "10%"] as const;
const DRAFT_WIDTHS = ["22%", "13%", "13%", "14%", "12%", "12%", "14%"] as const;
const INTENT_VALUES = [
  "Owned network",
  "Acquisition target",
  "Market watch",
  "Competitor benchmark",
  "Other",
] as const;

function localRowError(draft: {
  facility_name: string;
  address: string;
  ccn: string;
  npi: string;
  intent: string;
}): string | null {
  const errors: string[] = [];
  if (!draft.facility_name.trim()) errors.push("Facility name is required");
  if (!draft.address.trim()) errors.push("Address is required");
  if (!draft.ccn.trim() && !draft.npi.trim()) errors.push("CCN or NPI is required");
  if (!INTENT_VALUES.includes(draft.intent.trim() as (typeof INTENT_VALUES)[number])) {
    errors.push(`Intent must be one of: ${INTENT_VALUES.join(", ")}`);
  }
  return errors.length ? errors.join("; ") : null;
}

const editFieldSx = {
  width: "100%",
  minWidth: 0,
  m: 0,
  "& .MuiInputBase-root": {
    width: "100%",
    minWidth: 0,
    minHeight: 36,
    bgcolor: "#fff",
  },
  "& .MuiInputBase-input": {
    fontFamily: FN.fontFamily,
    fontSize: 14,
    lineHeight: 1.35,
    py: "8px",
    px: "8px",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
  },
};

const bodyCellSx = {
  p: "6px 8px",
  verticalAlign: "top",
  overflow: "hidden",
  minWidth: 0,
  maxWidth: 0,
};

function IdLine({ label, value }: { label: string; value?: string | null }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}>
      <Box
        component="span"
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: FN.figmaAccent,
          flexShrink: 0,
        }}
      >
        {label}
      </Box>
      <Box component="span" sx={{ overflowWrap: "anywhere" }}>
        {value || "—"}
      </Box>
    </Box>
  );
}

function EditField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <TextField
      size="small"
      fullWidth
      hiddenLabel
      placeholder={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ "aria-label": label }}
      sx={editFieldSx}
    />
  );
}

const SLA_HOURS = 72;

function rowDate(value: string | null | undefined, fallback: Date): Date {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
}

function formatStamp(value: Date): string {
  const formatted = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(value);
  return formatted.replace(/,([^,]*)$/, " at$1");
}

function facilityLabel(count: number): string {
  return `${count} ${count === 1 ? "facility" : "facilities"}`;
}

function ReviewStatusPill({ status }: { status?: string | null }) {
  const tone =
    status === "approved"
      ? { label: "Approved", bg: "#eef8f0", border: "#b7e1c0", color: "#0b5c12", dot: FN.figmaSuccess }
      : status === "disapproved"
        ? { label: "Disapproved", bg: "#f1f5f9", border: "#cbd5e1", color: "#334155", dot: "#475569" }
        : status === "canceled"
          ? { label: "Canceled", bg: "#fee2e2", border: "#fca5a5", color: "#991b1b", dot: "#991b1b" }
        : status === "needs_clarification"
          ? { label: "Needs clarification", bg: "#fffbeb", border: "#fde68a", color: "#92400e", dot: "#f59e0b" }
          : { label: "Pending review", bg: "#f7f7f7", border: FN.figmaCardBorder, color: FN.subtext, dot: FN.muted };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 99,
        border: `1px solid ${tone.border}`,
        bgcolor: tone.bg,
        fontFamily: FN.fontFamily,
        fontSize: 11,
        fontWeight: 600,
        color: tone.color,
        whiteSpace: "nowrap",
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: tone.dot }} />
      {tone.label}
    </Box>
  );
}

function SubmittedPortfolio({
  portfolio,
  justSubmitted,
  onDismissJustSubmitted,
}: {
  portfolio: NiPortfolio;
  justSubmitted?: boolean;
  onDismissJustSubmitted?: () => void;
}) {
  const navigate = useNavigate();
  const parsed = portfolio.submitted_at ? new Date(portfolio.submitted_at) : new Date();
  const submittedAt = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const elapsedMs = Math.max(0, Date.now() - submittedAt.getTime());
  const hoursIn = Math.round(elapsedMs / (60 * 60 * 1000));
  const hoursLeft = Math.max(0, SLA_HOURS - hoursIn);
  const barPct = Math.min(100, Math.max(hoursLeft === 0 ? 100 : 3, (elapsedMs / (SLA_HOURS * 60 * 60 * 1000)) * 100));
  const visibleRows = portfolio.rows.filter((row) => row.review_status !== "deleted");
  const count = visibleRows.length;
  const approvedCount = visibleRows.filter((row) => row.review_status === "approved").length;
  const canceledCount = visibleRows.filter(
    (row) => row.review_status === "canceled" || row.review_status === "disapproved",
  ).length;
  const clarificationRows = visibleRows.filter((row) => row.review_status === "needs_clarification");
  const clarificationCount = clarificationRows.length;
  const pendingCount = Math.max(0, count - approvedCount - canceledCount - clarificationCount);
  const changesRequested = clarificationCount > 0;
  const allApproved = count > 0 && approvedCount === count && pendingCount === 0 && !changesRequested;
  const email = (getCurrentUser()?.email || "").trim();
  const approvedShare = count > 0 ? Math.round((approvedCount / count) * 100) : 0;
  const headSx = {
    fontFamily: FN.fontFamily,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: FN.figmaAccent,
  };

  const slaTarget = new Date(submittedAt.getTime() + SLA_HOURS * 60 * 60 * 1000);
  return (
    <>
      {justSubmitted ? (
        <Box
          data-testid="ni-just-submitted"
          onClick={onDismissJustSubmitted}
          sx={{ cursor: "pointer",
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            px: 2.5,
            py: 2,
            borderRadius: FN.figmaCardRadius,
            border: "1px solid #b7e1c0",
            borderLeft: "3px solid #107c10",
            bgcolor: "#eef8f0",
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: "#107c10",
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ✓
          </Box>
          <Box>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, fontWeight: 600, color: "#0b5c12" }}>
              Portfolio submitted for review
            </Typography>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: "#0b5c12", mt: 0.25 }}>
              {`${facilityLabel(count)} · SLA target ${formatStamp(slaTarget)}`}
            </Typography>
          </Box>
        </Box>
      ) : null}
      {changesRequested ? (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            px: 2.5,
            py: 2,
            borderRadius: FN.figmaCardRadius,
            border: "1px solid #fde68a",
            bgcolor: "#fffbeb",
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: "#f59e0b",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            !
          </Box>
          <Box>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, fontWeight: 600, color: "#92400e", mb: 0.5 }}>
              {`${clarificationCount} ${clarificationCount === 1 ? "facility needs" : "facilities need"} clarification`}
            </Typography>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
              {`Our review team has questions on ${clarificationCount} of your ${count} submitted ${count === 1 ? "facility" : "facilities"}. We've emailed `}
              <Box component="strong" sx={{ fontWeight: 600 }}>
                {email || "you"}
              </Box>
              {" with specifics — respond with an updated CSV row to keep the review moving."}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              lineHeight: "32px",
            }}
          >
            {changesRequested
              ? "Portfolio · changes requested"
              : allApproved
                ? "Portfolio · approved"
                : "Portfolio · in review"}
          </Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.figmaAccent }}>
            {`Submitted ${formatStamp(submittedAt)} · ${facilityLabel(count)}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            onClick={() =>
              navigate("/dashboard/network/intel/upload", {
                state: { currentView: "network-intelligence" },
              })
            }
            sx={{
              textTransform: "none",
              fontFamily: FN.fontFamily,
              fontWeight: 600,
              color: FN.primary,
              flexShrink: 0,
            }}
          >
            Add facility list
          </Button>
          <Button
            onClick={() =>
              navigate("/dashboard/network/intel/manage", {
                state: { currentView: "network-intelligence" },
              })
            }
            sx={{
              textTransform: "none",
              fontFamily: FN.fontFamily,
              fontWeight: 600,
              color: FN.primary,
              flexShrink: 0,
            }}
          >
            Manage portfolio
          </Button>
        </Box>
      </Box>

      <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, color: FN.subtext, lineHeight: 1.65, maxWidth: 760 }}>
        {changesRequested
          ? `${approvedCount} ${approvedCount === 1 ? "facility has" : "facilities have"} been approved and will begin provisioning as soon as the flagged facilities are resolved. Approved facilities will go live even if the clarifications are still pending — you don't have to wait for the whole batch.`
          : allApproved
            ? "All facilities are approved. Your Intelligence dashboards are ready."
            : "Our team is actively validating your portfolio. You'll get an email when dashboards go live."}
      </Typography>

      {allApproved ? null : (
      <Box
        sx={{
          border: `1px solid ${FN.figmaCardBorder}`,
          borderRadius: FN.figmaCardRadius,
          boxShadow: FN.figmaCardShadow,
          px: 3,
          py: 2.5,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, gap: 2 }}>
          <Typography sx={headSx}>Review progress</Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, fontWeight: 500, color: FN.text }}>
            {`~${hoursIn} ${hoursIn === 1 ? "hour" : "hours"} in · ${hoursLeft} ${hoursLeft === 1 ? "hour" : "hours"} remaining${changesRequested ? " · pending your response" : ""}`}
          </Typography>
        </Box>
        <Box sx={{ height: 8, bgcolor: "#f3f4f6", borderRadius: 99, overflow: "hidden", mb: 1 }}>
          <Box
            sx={{
              width: `${changesRequested ? approvedShare : barPct}%`,
              height: "100%",
              background: changesRequested ? "linear-gradient(90deg, #fde68a, #f59e0b)" : GRADIENT,
              borderRadius: 99,
            }}
          />
        </Box>
        <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
          {changesRequested
            ? `${approvedCount} approved · ${clarificationCount} ${clarificationCount === 1 ? "needs" : "need"} clarification (awaiting your reply)`
            : `${approvedCount} of ${facilityLabel(count)} approved · ${pendingCount} still pending review`}
        </Typography>
      </Box>
      )}

      <Box
        sx={{
          border: `1px solid ${FN.figmaCardBorder}`,
          borderRadius: FN.figmaCardRadius,
          boxShadow: FN.figmaCardShadow,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 16, fontWeight: 600, color: FN.title }}>
            Submitted facilities
          </Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
            {changesRequested
              ? `${count} rows · ${approvedCount} approved · ${clarificationCount} need clarification`
              : allApproved
                ? `${count} rows · all approved`
                : `${count} rows · ${approvedCount} approved · ${pendingCount} pending review`}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f3fdff" }}>
              {["Facility", "CCN", "NPI", "Intent", "Note", "Submitted", "Review status"].map((label) => (
                <TableCell key={label} sx={headSx}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow
                key={`${row.portfolio_id ?? "p"}-${row.row_number}-${row.facility_name}`}
                sx={row.review_status === "needs_clarification" ? { bgcolor: "rgba(250, 199, 117, 0.08)" } : undefined}
              >
                <TableCell>
                  <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, fontWeight: 600, color: FN.text }}>
                    {row.facility_name || "—"}
                  </Typography>
                  <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 11, color: FN.muted }}>
                    {row.address || "—"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: FN.text }}>
                  {row.ccn || "—"}
                </TableCell>
                <TableCell sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: FN.text }}>
                  {row.npi || "—"}
                </TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      px: 1,
                      py: 0.25,
                      borderRadius: 99,
                      border: `1px solid ${FN.figmaCardBorder}`,
                      bgcolor: "#f7f7f7",
                      fontFamily: FN.fontFamily,
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    {row.intent || "—"}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      fontStyle: row.note ? "italic" : "normal",
                      color: row.note ? FN.subtext : FN.muted,
                    }}
                  >
                    {row.note || "—"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.text, whiteSpace: "nowrap" }}>
                  {formatStamp(rowDate(row.submitted_at, submittedAt)).replace(/ at.*$/, "")}
                </TableCell>
                <TableCell>
                  <ReviewStatusPill status={row.review_status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {changesRequested ? (
        <Box
          sx={{
            mt: 0.5,
            px: 2.5,
            py: 2,
            borderRadius: FN.figmaCardRadius,
            border: "1px solid #fde68a",
            borderLeft: "3px solid #f59e0b",
            bgcolor: "#fffbeb",
          }}
        >
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, fontWeight: 600, color: "#92400e", mb: 0.75 }}>
            What needs your attention
          </Typography>
          {clarificationRows.map((row) => (
            <Typography
              key={row.row_number}
              sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: "#92400e", lineHeight: 1.6, mb: 1.25, "&:last-child": { mb: 0 } }}
            >
              <Box component="strong" sx={{ fontWeight: 600 }}>
                {`${row.facility_name || "Facility"}${row.address ? ` (${row.address})` : ""}:`}
              </Box>
              {` ${row.clarification_reason || "Please reply with the corrected facility details."}`}
            </Typography>
          ))}
        </Box>
      ) : null}
    </>
  );
}

function seededPortfolio(state: unknown): NiPortfolio | null {
  if (!state || typeof state !== "object") return null;
  const record = state as { portfolio?: NiPortfolio; handoffToken?: unknown };
  if (!claimPortfolioHandoff(record.handoffToken)) return null;
  const portfolio = record.portfolio;
  if (!portfolio || typeof portfolio.id !== "number") return null;
  return portfolio;
}

export default function NetworkIntelligenceReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const seeded = seededPortfolio(location.state);
  const preferStatus =
    !!location.state &&
    typeof location.state === "object" &&
    (location.state as { portfolioView?: string }).portfolioView === "status";
  const [portfolio, setPortfolio] = useState<NiPortfolio | null>(seeded);
  const [savedDraft, setSavedDraft] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [justSubmitted, setJustSubmitted] = useState(searchParams.get("just_submitted") === "1");
  const [loading, setLoading] = useState(seeded == null);
  const [error, setError] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [rowDraft, setRowDraft] = useState({
    facility_name: "",
    address: "",
    ccn: "",
    npi: "",
    intent: "",
    note: "",
  });
  const [rowBusy, setRowBusy] = useState<number | null>(null);

  useEffect(() => {
    if (seeded) return undefined;
    let cancelled = false;
    const leave = () => {
      navigate("/dashboard/network/intel", {
        replace: true,
        state: { currentView: "network-intelligence" },
      });
    };
    const load = async () => {
      if (preferStatus) {
        const submittedPortfolio = await fetchSubmittedPortfolio();
        if (cancelled) return;
        if (submittedPortfolio) setPortfolio(submittedPortfolio);
        else leave();
        return;
      }
      const draft = await fetchIntelligenceDraft();
      if (cancelled) return;
      if (draft) {
        if (!portfolioHasVisibleFacilities(draft)) {
          leave();
          return;
        }
        setPortfolio(draft);
        setSavedDraft(true);
        return;
      }
      const submittedPortfolio = await fetchSubmittedPortfolio();
      if (cancelled) return;
      if (submittedPortfolio && portfolioHasVisibleFacilities(submittedPortfolio)) {
        setPortfolio(submittedPortfolio);
      } else leave();
    };
    load()
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load portfolio draft");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, preferStatus, seeded]);

  const dismissJustSubmitted = () => {
    setJustSubmitted(false);
    setSearchParams((prev) => {
      if (prev.get("just_submitted") !== "1") return prev;
      const next = new URLSearchParams(prev);
      next.delete("just_submitted");
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (!justSubmitted) return undefined;
    const timer = window.setTimeout(dismissJustSubmitted, 5000);
    const onScroll = () => dismissJustSubmitted();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [justSubmitted]);

  useEffect(() => {
    if (portfolio?.status !== "submitted") return undefined;
    const timer = window.setInterval(() => {
      fetchSubmittedPortfolio()
        .then((next) => {
          if (next) setPortfolio(next);
        })
        .catch(() => undefined);
    }, 60000);
    return () => window.clearInterval(timer);
  }, [portfolio?.status]);

  useEffect(() => {
    if (loading || !portfolio || portfolioHasVisibleFacilities(portfolio)) return;
    navigate("/dashboard/network/intel", {
      replace: true,
      state: { currentView: "network-intelligence" },
    });
  }, [loading, navigate, portfolio]);

  const goLanding = () => {
    navigate("/dashboard/network/intel", {
      state: { currentView: "network-intelligence" },
    });
  };

  const goUpload = () => {
    navigate("/dashboard/network/intel/upload", {
      state: { currentView: "network-intelligence" },
    });
  };

  const onSaveDraft = async () => {
    if (!portfolio) return;
    setError("");
    setSavingDraft(true);
    try {
      const saved = await saveIntelligenceDraft(portfolio);
      setPortfolio(saved);
      setSavedDraft(true);
      toast.success("Draft saved");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save portfolio draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const onSubmit = async () => {
    if (!portfolio) return;
    setError("");
    setSubmitting(true);
    try {
      const submittedPortfolio = await submitIntelligenceDraft(portfolio);
      setPortfolio(submittedPortfolio);
      setJustSubmitted(true);
      setSavedDraft(false);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("just_submitted", "1");
        return next;
      }, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not submit portfolio");
    } finally {
      setSubmitting(false);
    }
  };

  const submitted = portfolio?.status === "submitted";
  const warningCount = (portfolio?.rows ?? []).reduce(
    (total, row) => total + (row.warnings?.length ?? 0),
    0,
  );
  const canSubmit =
    portfolio != null &&
    !submitted &&
    editingRow == null &&
    portfolio.invalid_count === 0 &&
    portfolio.row_count > 0 &&
    portfolio.row_count <= portfolio.max_facilities;

  const startEdit = (row: NiPortfolioRow) => {
    setEditingRow(row.row_number);
    setRowDraft({
      facility_name: row.facility_name,
      address: row.address,
      ccn: row.ccn ?? "",
      npi: row.npi ?? "",
      intent: row.intent,
      note: row.note ?? "",
    });
    setError("");
  };

  const applyLocalRow = (rowNumber: number) => {
    const errorReason = localRowError(rowDraft);
    setPortfolio((current) => {
      if (!current) return current;
      const rows = current.rows.map((row) =>
        row.row_number === rowNumber
          ? {
              ...row,
              facility_name: rowDraft.facility_name,
              address: rowDraft.address,
              ccn: rowDraft.ccn.trim() || null,
              npi: rowDraft.npi.trim() || null,
              intent: rowDraft.intent,
              note: rowDraft.note.trim() || null,
              is_valid: errorReason == null,
              error_reason: errorReason,
            }
          : row,
      );
      const validCount = rows.filter((row) => row.is_valid).length;
      return {
        ...current,
        rows,
        row_count: rows.length,
        valid_count: validCount,
        invalid_count: rows.length - validCount,
      };
    });
    setEditingRow(null);
  };

  const saveRow = async (rowNumber: number) => {
    if (!savedDraft || portfolio == null || portfolio.id <= 0) {
      applyLocalRow(rowNumber);
      return;
    }
    setRowBusy(rowNumber);
    setError("");
    try {
      const updated = await updateIntelligenceRow(rowNumber, {
        facility_name: rowDraft.facility_name,
        address: rowDraft.address,
        ccn: rowDraft.ccn.trim() || null,
        npi: rowDraft.npi.trim() || null,
        intent: rowDraft.intent,
        note: rowDraft.note.trim() || null,
      });
      setPortfolio(updated);
      setSavedDraft(true);
      setEditingRow(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save facility row");
    } finally {
      setRowBusy(null);
    }
  };

  const dropRowLocally = (rowNumber: number) => {
    setPortfolio((current) => {
      if (!current) return current;
      const rows = current.rows.filter((row) => row.row_number !== rowNumber);
      const validCount = rows.filter((row) => row.is_valid).length;
      return {
        ...current,
        rows,
        row_count: rows.length,
        valid_count: validCount,
        invalid_count: rows.length - validCount,
      };
    });
    if (editingRow === rowNumber) setEditingRow(null);
  };

  const removeRow = async (rowNumber: number) => {
    if (!savedDraft || portfolio == null || portfolio.id <= 0) {
      dropRowLocally(rowNumber);
      return;
    }
    setRowBusy(rowNumber);
    setError("");
    try {
      const updated = await deleteIntelligenceRow(rowNumber);
      setPortfolio(updated);
      if (editingRow === rowNumber) setEditingRow(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not remove facility row";
      if (message.startsWith("NI_NO_DRAFT")) {
        dropRowLocally(rowNumber);
        return;
      }
      setError(message);
    } finally {
      setRowBusy(null);
    }
  };

  if (loading) {
    return (
      <FacilityNetworkPageShell>
        <NetworkIntelligenceLoader />
      </FacilityNetworkPageShell>
    );
  }

  return (
    <FacilityNetworkPageShell>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", minWidth: 0 }}>
        <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
          <Button
            onClick={goLanding}
            sx={{
              p: 0,
              minWidth: 0,
              textTransform: "none",
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.primary,
            }}
          >
            Intelligence
          </Button>
          {submitted ? " › Portfolio status" : " › Submit portfolio"}
        </Typography>

        {submitted ? null : (
        <Box>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              lineHeight: "32px",
            }}
          >
            Review your portfolio
          </Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.figmaAccent }}>
            Confirm parsed rows before submitting for review
          </Typography>
        </Box>
        )}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {portfolio && submitted ? (
          <SubmittedPortfolio
            portfolio={portfolio}
            justSubmitted={justSubmitted}
            onDismissJustSubmitted={dismissJustSubmitted}
          />
        ) : null}

        {portfolio && !submitted ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.25,
                bgcolor: portfolio.invalid_count > 0 ? "#fff8e6" : "#eef8f0",
                border: `1px solid ${portfolio.invalid_count > 0 ? "#f5d90a" : "#b7e1c0"}`,
                borderLeft: `3px solid ${portfolio.invalid_count > 0 ? "#ed6c02" : FN.figmaSuccess}`,
                borderRadius: "4px",
              }}
            >
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.text }}>
                <strong>{portfolio.source_filename}</strong>
                {` · parsed ${portfolio.row_count} facilities · ${portfolio.valid_count} valid`}
                {portfolio.invalid_count > 0 ? ` · ${portfolio.invalid_count} invalid` : " · all rows valid"}
              </Typography>
              <Button
                onClick={goUpload}
                sx={{
                  textTransform: "none",
                  fontFamily: FN.fontFamily,
                  fontWeight: 600,
                  color: FN.primary,
                }}
              >
                Re-upload
              </Button>
            </Box>

            <Box
              sx={{
                border: `1px solid ${FN.figmaCardBorder}`,
                borderRadius: FN.figmaCardRadius,
                boxShadow: FN.figmaCardShadow,
                overflow: "auto",
              }}
            >
              <Table size="small" sx={{ tableLayout: "fixed", width: "100%", minWidth: 960 }}>
                <colgroup>
                  {(savedDraft ? DRAFT_WIDTHS : PREVIEW_WIDTHS).map((width, index) => (
                    <col key={`${width}-${index}`} style={{ width }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f3fdff" }}>
                    {(savedDraft
                      ? [
                          ["Facility", DRAFT_WIDTHS[0]],
                          ["Address", DRAFT_WIDTHS[1]],
                          ["CCN / NPI", DRAFT_WIDTHS[2]],
                          ["Intent", DRAFT_WIDTHS[3]],
                          ["Note", DRAFT_WIDTHS[4]],
                          ["Status", DRAFT_WIDTHS[5]],
                          ["Actions", DRAFT_WIDTHS[6]],
                        ]
                      : [
                          ["Facility", PREVIEW_WIDTHS[0]],
                          ["Address", PREVIEW_WIDTHS[1]],
                          ["CCN / NPI", PREVIEW_WIDTHS[2]],
                          ["Intent", PREVIEW_WIDTHS[3]],
                          ["Note", PREVIEW_WIDTHS[4]],
                          ["Actions", PREVIEW_WIDTHS[5]],
                        ]
                    ).map(([label, width]) => (
                      <TableCell
                        key={label}
                        sx={{
                          width,
                          fontFamily: FN.fontFamily,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: FN.figmaAccent,
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolio.rows.map((row) => {
                    const editing = editingRow === row.row_number;
                    return (
                    <TableRow
                      key={row.row_number}
                      sx={{ bgcolor: row.is_valid ? "#fff" : FN.errorBg }}
                    >
                      <TableCell sx={bodyCellSx}>
                        {editing ? (
                          <EditField
                            label="Facility"
                            value={rowDraft.facility_name}
                            onChange={(value) =>
                              setRowDraft((current) => ({ ...current, facility_name: value }))
                            }
                          />
                        ) : (
                          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, fontWeight: 600 }}>
                            {row.facility_name || "—"}
                          </Typography>
                        )}
                        {!editing && row.error_reason ? (
                          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaError }}>
                            {row.error_reason}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          fontFamily: FN.fontFamily,
                          fontSize: 12,
                          color: FN.subtext,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {editing ? (
                          <EditField
                            label="Address"
                            value={rowDraft.address}
                            onChange={(value) =>
                              setRowDraft((current) => ({ ...current, address: value }))
                            }
                          />
                        ) : (
                          row.address || "—"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          fontFamily: "ui-monospace, monospace",
                          fontSize: 12,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {editing ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0, width: "100%" }}>
                            <EditField
                              label="CCN"
                              value={rowDraft.ccn}
                              onChange={(value) =>
                                setRowDraft((current) => ({ ...current, ccn: value }))
                              }
                            />
                            <EditField
                              label="NPI"
                              value={rowDraft.npi}
                              onChange={(value) =>
                                setRowDraft((current) => ({ ...current, npi: value }))
                              }
                            />
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            <IdLine label="CCN" value={row.ccn} />
                            <IdLine label="NPI" value={row.npi} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          fontFamily: FN.fontFamily,
                          fontSize: 12,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {editing ? (
                          <EditField
                            label="Intent"
                            value={rowDraft.intent}
                            onChange={(value) =>
                              setRowDraft((current) => ({ ...current, intent: value }))
                            }
                          />
                        ) : (
                          row.intent || "—"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          fontFamily: FN.fontFamily,
                          fontSize: 12,
                          color: row.note ? FN.subtext : FN.figmaAccent,
                          fontStyle: row.note ? "italic" : "normal",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {editing ? (
                          <EditField
                            label="Note"
                            value={rowDraft.note}
                            onChange={(value) =>
                              setRowDraft((current) => ({ ...current, note: value }))
                            }
                          />
                        ) : (
                          row.note || "—"
                        )}
                      </TableCell>
                      {savedDraft ? (
                        <TableCell sx={bodyCellSx}>
                          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, fontWeight: 600, color: FN.text }}>
                            Draft
                          </Typography>
                        </TableCell>
                      ) : null}
                      <TableCell sx={{ ...bodyCellSx, overflow: "visible" }}>
                        {submitted ? null : (
                          <Box sx={{ display: "flex", gap: 0.25 }}>
                            {editing ? (
                              <IconButton
                                aria-label={`Save row ${row.row_number}`}
                                size="small"
                                disabled={rowBusy === row.row_number}
                                onClick={() => void saveRow(row.row_number)}
                                sx={{ color: FN.primary }}
                              >
                                <SaveOutlinedIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                aria-label={`Edit row ${row.row_number}`}
                                size="small"
                                disabled={rowBusy != null}
                                onClick={() => startEdit(row)}
                                sx={{ color: FN.primary }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              aria-label={
                                editing
                                  ? `Close edit mode for row ${row.row_number}`
                                  : `Remove row ${row.row_number}`
                              }
                              title={
                                editing
                                  ? "This will close edit mode"
                                  : "This will remove this record"
                              }
                              size="small"
                              disabled={rowBusy === row.row_number}
                              onClick={() => {
                                if (editing) {
                                  setEditingRow(null);
                                  setError("");
                                  return;
                                }
                                void removeRow(row.row_number);
                              }}
                              sx={{ color: editing ? FN.muted : FN.figmaError }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
              {`${portfolio.row_count} of ${portfolio.max_facilities} slots used`}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                pt: 1,
                borderTop: `1px solid ${FN.panelBorder}`,
              }}
            >
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent, maxWidth: 520 }}>
                Once submitted, our review team validates each facility and gets back to you within
                72 hours. If any need clarification, we&apos;ll email you with the specific rows to
                update.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  disabled={submitted || savingDraft}
                  onClick={() => void onSaveDraft()}
                  sx={{ textTransform: "none", fontFamily: FN.fontFamily, color: FN.subtext }}
                >
                  {savingDraft ? "Saving…" : "Save as draft"}
                </Button>
                <Button
                  disabled={!canSubmit || submitting}
                  onClick={() => void onSubmit()}
                  sx={{
                    textTransform: "none",
                    fontFamily: FN.fontFamily,
                    fontWeight: 600,
                    color: "#fff",
                    background: GRADIENT,
                    "&.Mui-disabled": { color: "#fff", opacity: 0.5 },
                  }}
                >
                  {submitting
                    ? "Submitting…"
                    : warningCount > 0
                      ? `Submit with ${warningCount} warnings`
                      : "Submit for review"}
                </Button>
              </Box>
            </Box>
          </>
        ) : null}
      </Box>
    </FacilityNetworkPageShell>
  );
}
